import { ValidationRule } from '../types';

/**
 * `points` is one entry that expands at runtime into the range-scaled `point_*` fields,
 * so its keys are not known when the form is built.
 */
export type LibraryEntryKind = 'field' | 'frequency' | 'temperature' | 'points';

export const ENTRY_KIND_LABELS: Record<LibraryEntryKind, string> = {
  field: 'Field',
  frequency: 'Frequency',
  temperature: 'Temperature',
  points: 'Calibration points',
};

export interface PointSequence {
  key: string;
  values: number[];
  range_scaled?: boolean;
  validation?: ValidationRule[];
}

export interface LibraryEntry {
  kind: LibraryEntryKind;
  key: string;
  label: string;
  field_type: string;
  placeholder: string;
  validation: ValidationRule[];
  meta?: { sequences?: PointSequence[] } | null;
  sort_order?: number;
  is_active: boolean;
}

export interface LibrarySummary {
  sensor_type: string;
  category: string;
  published_version?: number | null;
  has_draft: boolean;
  entry_count: number;
  updated_at?: string | null;
  updated_by?: string | null;
}

export interface LibraryVersionItem {
  version: number;
  status: 'draft' | 'published' | 'archived';
  note?: string | null;
  restored_from?: number | null;
  created_by?: string | null;
  created_at: string;
  published_at?: string | null;
  published_by?: string | null;
  published_by_name?: string | null;
}

export interface LibraryDetail extends LibraryVersionItem {
  sensor_type: string;
  category: string;
  fields: LibraryEntry[];
}

export interface LibraryAuditLogItem {
  action: 'draft_created' | 'draft_saved' | 'draft_discarded' | 'published' | 'rolled_back';
  version?: number | null;
  actor_id?: string | null;
  actor_name?: string | null;
  details?: Record<string, any> | null;
  created_at: string;
}

/** A published entry a form can select. */
export interface LibraryCatalogEntry {
  key: string;
  label: string;
  kind: LibraryEntryKind;
  type: string;
  placeholder: string;
  validation: ValidationRule[];
  is_group: boolean;
  /** Keys the entry produces; empty for a points group (depends on the range). */
  preview_keys: string[];
}

export interface LibraryRef { sensor_type: string; category: string; }

/** Published forms that select entries a publish or rollback would remove (API 409 `LIBRARY_IN_USE`). */
export interface LibraryImpact {
  message: string;
  removed_entries: string[];
  forms: { form_id: string; name: string; version: number; entries: string[] }[];
}

/** A field as a running form receives it from the runtime schema endpoint. */
export interface PreviewField {
  key: string;
  label: string;
  type: string;
  placeholder: string;
  validation: ValidationRule[];
}
